import React, { useState, useRef, Fragment } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  Container,
  Typography,
  CircularProgress,
  Alert,
  CssBaseline,
  Box,
  Snackbar,
  Paper,
} from "@mui/material";
import api from "./api";
import { INITIALSTATE, PREMIUM, SignInStatus } from "./states";
import DownloadButtons from "./downloadButtons";
import PdfUsageNote from "./pdfUsageNote";
import { CHROME_EXTENSION_URL, TranslatedFiles } from "./links";
import { User } from "./app";

interface Props {
  user: User | null;
  signInStatus: SignInStatus;
  expire: number;
  accessToken: string;
}

const Pro = (props: Props) => {
  const inputRef = useRef<any>(null);
  const [showSnack, setShowSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState<any>();
  const [state, setState] = useState(0);
  const [files, setFiles] = useState<TranslatedFiles>();
  const processFile = (source: File) => {
    if (!source) {
      return;
    }
    setState(1);
    const params = new FormData();
    params.append("file", source);
    params.append("token", props.accessToken);
    params.append("original_filename", source.name);
    api
      .post("auto/", params)
      .then((response) => {
        if (response.status !== 200) {
          setSnackMessage(
            "出现意外错误。您上传的文件可能不符合 Readable 的标准。"
          );
          setState(0);
          setShowSnack(true);
        } else if ("sizeover" in response.data) {
          setSnackMessage("文件大小过大，请分成 100 页，大小不超过 50 MB。");
          setState(0);
          setShowSnack(true);
        } else if ("undefined" in response.data) {
          setState(0);
        } else if ("error" in response.data) {
          setSnackMessage(
            "出现意外错误。您上传的文件可能不符合 Readable 的标准。"
          );
          setState(0);
          setShowSnack(true);
        } else if ("notext" in response.data) {
          setSnackMessage(
            "未检测到文本。请尝试在PDF阅读器中打开该文件，拖动鼠标确认是否有可以选中的文本。您可以尝试对文件进行分割，如果上传的文件中没有识别到文本，则Readable无法进行翻译。您也可以尝试寻找与该文件内容相同的其他PDF版本。"
          );
          setState(0);
          setShowSnack(true);
        } else if ("notpdf" in response.data) {
          setSnackMessage("仅支持 PDF 文件。");
          setState(0);
          setShowSnack(true);
        } else {
          setFiles({
            ja: response.data.ja,
            alt: response.data.alt,
            print: response.data.print,
          });
          setState(2);
        }
      })
      .catch((e) => {
        if (e.response.status === 413) {
          setSnackMessage("文件大小过大，请分成 100 页，大小不超过 50 MB。");
          setState(0);
          setShowSnack(true);
        } else {
          setSnackMessage(
            "出现意外错误。您上传的文件可能不符合 Readable 的标准。"
          );
          setState(0);
          setShowSnack(true);
        }
      });
  };
  const onDrop = (e: DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // @ts-ignore
    const files = e.dataTransfer.files;
    if ((state === 0 || state >= 2) && files.length === 1 && files[0]) {
      processFile(files[0]);
    }
  };
  const onDragOver = (e: DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };
  const onFileInputChange = (e: any) => {
    const source = e.target.files[0];
    if (!source) {
      return;
    }
    processFile(source);
  };
  if (props.signInStatus === INITIALSTATE) {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
      </Container>
    );
  }
  if (props.signInStatus !== PREMIUM) {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Typography
          component="h2"
          variant="body1"
          color="text.primary"
          align="center"
          sx={{ mt: 4 }}
        >
          您尚未注册专业版
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "baseline",
          }}
        >
          <Button
            variant="contained"
            to="/pricing"
            sx={{ mt: 4, width: 200, height: 40 }}
            component={RouterLink}
          >
            立即注册
          </Button>
        </Box>
      </Container>
    );
  }
  return (
    <Fragment>
      {/* @ts-ignore */}
      <Container
        component="main"
        sx={{ mb: 2 }}
        maxWidth="lg"
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            fontWeight="fontWeightBold"
            sx={{ mb: 2 }}
          >
            1. 请上传需要翻译的PDF文件
          </Typography>
          <img src="./imgs/upload.png" alt="上传" width="200" />
          {(state === 0 || state >= 2) && (
            <Button
              variant="contained"
              sx={{ mt: 2, mb: 4, width: 200, height: 40 }}
              onClick={() => {
                inputRef.current.click();
              }}
            >
              上传 PDF 文件
            </Button>
          )}
          {state === 1 && (
            <CircularProgress sx={{ mt: 2, mb: 4, height: 40 }} />
          )}
          <Typography
            variant="h4"
            component="h1"
            fontWeight="fontWeightBold"
            sx={{ mb: 4 }}
          >
            2. 下载翻译结果
          </Typography>
          {state < 2 && (
            <img
              src="./imgs/download_disable.png"
              alt="下载翻译结果"
              width="300"
            />
          )}
          {state >= 2 && (
            <img src="./imgs/download.png" alt="下载翻译结果" width="300" />
          )}
          <DownloadButtons files={state >= 2 ? files : undefined} />
          <PdfUsageNote />
          <Paper
            variant="outlined"
            sx={{ mt: 3, mb: 3, p: 3, width: "100%", maxWidth: 600 }}
          >
            <Typography component="p">
              安装Chrome扩展程序后，您可以直接在浏览器中点击图标翻译PDF文件，无需打开Readable网站。
            </Typography>
            <Typography component="p">
              <a href={CHROME_EXTENSION_URL} target="_blank" rel="noreferrer">
                安装Chrome浏览器扩展程序
              </a>
            </Typography>
            <Typography component="p">
              安装扩展程序后，您的翻译体验将更加便捷。
            </Typography>
            <Typography component="p">使用方法：</Typography>
            <ol>
              <li>在Chrome浏览器中打开PDF文件</li>
              <li>点击变为蓝色的扩展程序图标</li>
              <li>如果未登录Readable，请先登录</li>
              <li>图标变为浅蓝色时表示正在翻译，请稍候</li>
              <li>翻译完成后，译文将在新标签页中打开</li>
            </ol>
          </Paper>
          {props.expire !== 0 && (
            <Typography component="p">
              账户于 {new Date(props.expire * 1000).getFullYear()} 年{" "}
              {new Date(props.expire * 1000).getMonth() + 1} 月{" "}
              {new Date(props.expire * 1000).getDate()} 日到期。
            </Typography>
          )}
        </Box>
      </Container>
      <input
        hidden
        type="file"
        onChange={onFileInputChange}
        accept={
          /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
            ? undefined
            : ".pdf"
        }
        ref={inputRef}
      />
      <Snackbar
        open={showSnack}
        autoHideDuration={6000}
        onClose={() => setShowSnack(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnack(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </Fragment>
  );
};

export default Pro;
